import React, { useState } from 'react';
import { Destination } from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetchImmutable } from '@marageti/z4-lib';
import TravelerProfileBucketListView from './traveler-profile-bucket-list-view';
import useDestinations from '../../hooks/use-destinations';
import TravelerProfileBucketListModal from './traveler-profile-bucket-list-modal';
import useTravelerProfile from '../../hooks/use-traveler-profile';

export type TravelerBucketListContainerProps = {
  travelerId: string | undefined;
  canEdit: boolean;
};

const TravelerBucketListContainer = ({
  travelerId,
  canEdit,
}: TravelerBucketListContainerProps) => {
  const apiClient = useApiClient();

  const { travelerProfile, updateTravelerProfile } = useTravelerProfile(travelerId);
  const desiredDestinations = travelerProfile?.desiredDestinations ?? [];
  const { destinationsList } = useDestinations(desiredDestinations.flatMap((dest) => dest.destinationIDs));

  const {
    data,
    isLoading: isDestinationListLoading,
  } = useFetchImmutable<Destination[]>(
    apiClient.destinationsClient.getDestinations,
    [{ params: { all: true } }],
    true,
  );
  const allDestinations = data?.sort((a, b) => a.name.localeCompare(b.name));

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSave = async (destIds: string[]) => {
    const newDesiredDestinations = destIds.map((id) => ({
      countries: allDestinations?.find((d) => d.id === id)?.countries ?? [],
      destinationIDs: [id],
      dateRequested: new Date().toISOString(),
    }));
    await updateTravelerProfile(
      {
        desiredDestinations: newDesiredDestinations,
      },
      'Could not save bucket list destinations.',
      'Bucket list destinations saved.',
    );
  };

  return (
    <>
      <TravelerProfileBucketListView
        destinations={destinationsList}
        onEdit={canEdit ? handleOpenModal : undefined}
      />
      <TravelerProfileBucketListModal
        destinationsList={allDestinations || []}
        initialDestinations={destinationsList}
        isLoading={isDestinationListLoading}
        onClose={handleCloseModal}
        onSubmit={handleSave}
        open={isModalOpen}
      />
    </>
  );
};

export default TravelerBucketListContainer;
